import Button from "core/buttons/button/Button";
import StaffCorporateInfoForm from "components/form/staff/StaffCorporateInfoForm";
import InfoContext from "pages/InfoContext";
import StaffPersonalInfoForm from "components/form/staff/StaffPersonalInfoForm";
import ResidencePermitForm from "components/form/residence-permit/ResidencePermitForm";
import _ from "lodash";
import { FIELD_REQUIRED } from "constants/popup-codes";
import { FormattedMessage, useIntl } from "react-intl";
import { POPUP_STATUS, QUERY_STATUS } from "constants/constants";
import { formatReceivedData } from "utils/staffHelper";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useUserContext } from "api/user/UserProvider";
import {
  STAFF_FORM_DEFAULT_VALUES,
  STAFF_FORM_DEFAULT_EDITION_EXTRA_VALUES,
  STAFF_FORM_LABELS,
  STAFF_FORM_PLACEHOLDERS,
} from "constants/forms";
import propTypes from "prop-types";
import colors from "assets/styles/mixins/colors.scss";

const PersonalStaff = ({
  readOnly,
  setReadOnly,
  onSubmit,
  initHeader,
  getStatus,
  getCode,
}) => {
  const {
    branches,
    davFunctions,
    lang,
    leavingReasons,
    recruitmentMethodList,
    userBySlug,
    profileList,
    roleList,
    setIsValidated,
    editStaffStatus,
    nationalities,
    apiErrorResponse,
    isValidationModalOpen,
    personsByProfileList,
  } = useContext(InfoContext);
  const intl = useIntl();

  const defaultValues = {
    ...STAFF_FORM_DEFAULT_VALUES,
    ...STAFF_FORM_DEFAULT_EDITION_EXTRA_VALUES,
  };

  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    unregister,
    watch,
    setError,
  } = useForm({
    defaultValues,
  });
  const [isVisa, setIsVisa] = useState(false);
  const [showSection, setShowSection] = useState(false);

  const apiErrorFieldsRef = useRef([]);
  // Synchronous lock: blocks re-entrant submits before React re-renders the disabled button
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (apiErrorResponse && Object.keys(apiErrorResponse).length > 0) {
      Object.entries(apiErrorResponse).forEach(([field, message]) => {
        setError(field, { type: "manual", message });
      });
      apiErrorFieldsRef.current = Object.keys(apiErrorResponse);
      setReadOnly(false);
    } else if (apiErrorFieldsRef.current.length > 0) {
      apiErrorFieldsRef.current.forEach((field) => clearErrors(field));
      apiErrorFieldsRef.current = [];
    }
  }, [apiErrorResponse, setError]);

  useEffect(() => {
    setIsVisa(userBySlug.visa);
  }, [userBySlug]);

  const initForm = () => {
    const defaultKeys = Object.keys(defaultValues);
    const formattedReceivedData = formatReceivedData(
      {
        ...userBySlug,
      },
      {
        branches,
        davFunctions,
        lang,
        leavingReasons,
        recruitmentMethodList,
        profileList,
        role: roleList,
        nationalities,
        users: personsByProfileList,
        intl,
      }
    );
    Object.keys(formattedReceivedData).forEach((elm) => {
      const value = formattedReceivedData[elm];
      if (defaultKeys.includes(elm) && value !== undefined) {
        setValue(elm, value);
      }
    });
  };

  useEffect(() => {
    if (
      !_.isEmpty(userBySlug) &&
      !isValidationModalOpen &&
      editStaffStatus !== QUERY_STATUS.ERROR &&
      editStaffStatus !== QUERY_STATUS.LOADING
    ) {
      initForm();
    }
  }, [userBySlug, isValidationModalOpen, editStaffStatus]);

  const { setApiErrorResponse } = useUserContext();

  const onReset = () => {
    initForm();
    initHeader();
    setReadOnly(true);
    clearErrors();
    setApiErrorResponse(null);
    window.scrollTo(0, 0);
    setShowSection(isVisa);
  };
  const onError = (errors) => {
    if (!_.isEmpty(errors)) {
      getStatus(POPUP_STATUS.ERROR);
      getCode(FIELD_REQUIRED);
    }
  };

  const onGuardedSubmit = async (info) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await onSubmit(info);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <form onSubmit={handleSubmit(onGuardedSubmit, onError)}>
      <div className="section-2">
        <StaffPersonalInfoForm
          readOnly={readOnly}
          setValue={(field, value) => {
            setValue(field, value);
            clearErrors(field);
          }}
          watch={watch}
          control={control}
          register={register}
          errors={errors}
          clearErrors={clearErrors}
          unregister={unregister}
        />
        <br />
        <ResidencePermitForm
          isVisa={isVisa}
          showSection={showSection}
          setShowSection={setShowSection}
          readOnly={readOnly}
          setValue={(field, value) => {
            setValue(field, value);
            clearErrors(field);
          }}
          watch={watch}
          control={control}
          register={register}
          errors={errors}
          labels={STAFF_FORM_LABELS}
          placeholders={STAFF_FORM_PLACEHOLDERS}
        />
      </div>
      <StaffCorporateInfoForm
        readOnly={readOnly}
        setValue={(field, value) => {
          setValue(field, value);
          clearErrors(field);
        }}
        watch={watch}
        control={control}
        register={register}
        errors={errors}
        unregister={unregister}
        clearErrors={clearErrors}
      />
      {!readOnly && (
        <div style={{ marginTop: "50px" }} className="form-buttons">
          <Button
            className="btn-standard btn-outline"
            type="button"
            onClick={onReset}
            id="button-form-reset"
          >
            <FormattedMessage id="cancel" defaultMessage="Annuler" />
          </Button>
          <Button
            color="btn-medium gray-700"
            type="submit"
            id="button-form-validation"
            onClick={() => setIsValidated(true)}
            backgroundColor={
              editStaffStatus === QUERY_STATUS.LOADING && colors.disabledGrey
            }
            disabled={editStaffStatus === QUERY_STATUS.LOADING}
          >
            <FormattedMessage
              id="button_form_validation_edition"
              defaultMessage="Valider l'édition"
            />
          </Button>
        </div>
      )}
    </form>
  );
};

export default PersonalStaff;

PersonalStaff.propTypes = {
  readOnly: propTypes.bool,
  setReadOnly: propTypes.func,
  onSubmit: propTypes.func,
  initHeader: propTypes.func,
  getStatus: propTypes.func,
  getCode: propTypes.func,
  setIsValidated: propTypes.func,
};
